import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Database, Merge, CheckCircle, 
  Loader2, Search, Wheat, PawPrint
} from 'lucide-react';
import { 
  useBrandAliases, 
  useMergeBrandAlias, 
  useFoodBrands,
  useFarmFeedCategories,
  useCategorizeFarmFeed,
  BrandAlias 
} from '@/hooks/useNutritionAdmin';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const NutritionBrandNormalization: React.FC = () => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState<BrandAlias | null>(null);
  const [selectedCanonicalBrand, setSelectedCanonicalBrand] = useState<string>('');
  const [selectedFeedCategory, setSelectedFeedCategory] = useState<string>('');

  const { data: aliases, isLoading } = useBrandAliases(statusFilter);
  const { data: brands } = useFoodBrands();
  const { data: feedCategories } = useFarmFeedCategories();
  const mergeBrand = useMergeBrandAlias();
  const categorizeFeed = useCategorizeFarmFeed();

  const filteredAliases = aliases?.filter(alias => 
    alias.alias_text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenMergeDialog = (alias: BrandAlias) => {
    setSelectedAlias(alias);
    setSelectedCanonicalBrand('');
    setSelectedFeedCategory('');
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!selectedAlias) return;
    
    if (selectedAlias.is_farm_animal) {
      if (!selectedFeedCategory) return;
      await categorizeFeed.mutateAsync({
        aliasId: selectedAlias.id,
        feedCategoryId: parseInt(selectedFeedCategory),
      });
      const category = feedCategories?.find(c => c.id === parseInt(selectedFeedCategory));
      toast.success(t('nutritionAdmin.normalization.feedCategorized', { 
        alias: selectedAlias.alias_text, 
        category: category?.name || selectedFeedCategory 
      }));
    } else {
      if (!selectedCanonicalBrand) return;
      await mergeBrand.mutateAsync({
        aliasId: selectedAlias.id,
        canonicalBrandId: parseInt(selectedCanonicalBrand),
      });
    }
    
    setMergeDialogOpen(false);
    setSelectedAlias(null);
    setSelectedCanonicalBrand('');
    setSelectedFeedCategory('');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-amber-600 border-amber-300">{t('nutritionAdmin.normalization.statusPending')}</Badge>;
      case 'merged':
        return <Badge variant="outline" className="text-green-600 border-green-300">{t('nutritionAdmin.normalization.statusMerged')}</Badge>;
      case 'ignored':
        return <Badge variant="outline" className="text-muted-foreground">{t('nutritionAdmin.normalization.statusIgnored')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCanonicalDisplay = (alias: BrandAlias) => {
    if (alias.canonical_brand_name) {
      return (
        <span className="flex items-center gap-1 text-green-600">
          <PawPrint className="h-3 w-3" />
          {alias.canonical_brand_name}
        </span>
      );
    }
    if (alias.canonical_feed_category_name) {
      return (
        <span className="flex items-center gap-1 text-emerald-600">
          <Wheat className="h-3 w-3" />
          {alias.canonical_feed_category_name}
        </span>
      );
    }
    return <span className="text-muted-foreground">—</span>;
  };

  const isPending = mergeBrand.isPending || categorizeFeed.isPending;
  const canSubmit = selectedAlias?.is_farm_animal 
    ? !!selectedFeedCategory 
    : !!selectedCanonicalBrand;

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Database className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-700 dark:text-blue-300">
                {t('nutritionAdmin.normalization.infoTitle')}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {t('nutritionAdmin.normalization.infoDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            {t('nutritionAdmin.normalization.queueTitle')}
          </CardTitle>
          <CardDescription>
            {t('nutritionAdmin.normalization.queueDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('nutritionAdmin.normalization.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={t('nutritionAdmin.normalization.statusLabel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t('nutritionAdmin.normalization.filterPending')}</SelectItem>
                <SelectItem value="merged">{t('nutritionAdmin.normalization.filterMerged')}</SelectItem>
                <SelectItem value="ignored">{t('nutritionAdmin.normalization.filterIgnored')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredAliases && filteredAliases.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('nutritionAdmin.normalization.tableInputText')}</TableHead>
                    <TableHead className="text-center">{t('nutritionAdmin.normalization.tableType')}</TableHead>
                    <TableHead className="text-center">{t('nutritionAdmin.normalization.tableOccurrences')}</TableHead>
                    <TableHead>{t('nutritionAdmin.normalization.tableStatus')}</TableHead>
                    <TableHead>{t('nutritionAdmin.normalization.tableCanonicalBrand')}</TableHead>
                    <TableHead className="text-right">{t('nutritionAdmin.normalization.tableActions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAliases.map((alias) => (
                    <TableRow key={alias.id}>
                      <TableCell className="font-medium">
                        <code className="bg-muted px-2 py-1 rounded text-sm">
                          {alias.alias_text}
                        </code>
                      </TableCell>
                      <TableCell className="text-center">
                        {alias.is_farm_animal ? (
                          <Badge variant="secondary" className="gap-1">
                            <Wheat className="h-3 w-3" />
                            {t('nutritionAdmin.normalization.farmAnimal')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1">
                            <PawPrint className="h-3 w-3" />
                            {t('nutritionAdmin.normalization.pet')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">
                          {alias.occurrence_count}x
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(alias.status)}
                      </TableCell>
                      <TableCell>
                        {getCanonicalDisplay(alias)}
                      </TableCell>
                      <TableCell className="text-right">
                        {alias.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenMergeDialog(alias)}
                            >
                              <Merge className="h-4 w-4 mr-1" />
                              {alias.is_farm_animal 
                                ? t('nutritionAdmin.normalization.categorizeButton')
                                : t('nutritionAdmin.normalization.mergeButton')
                              }
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
              <p>
                {statusFilter === 'pending' 
                  ? t('nutritionAdmin.normalization.noPending')
                  : t('common.noResults')
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merge/Categorize Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedAlias?.is_farm_animal 
                ? t('nutritionAdmin.normalization.dialogTitleFarm')
                : t('nutritionAdmin.normalization.dialogTitle')
              }
            </DialogTitle>
            <DialogDescription>
              {selectedAlias?.is_farm_animal 
                ? t('nutritionAdmin.normalization.dialogDescriptionFarm')
                : t('nutritionAdmin.normalization.dialogDescription')
              }
            </DialogDescription>
          </DialogHeader>

          {selectedAlias && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {selectedAlias.is_farm_animal ? (
                    <Badge variant="secondary" className="gap-1">
                      <Wheat className="h-3 w-3" />
                      {t('nutritionAdmin.normalization.farmAnimal')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1">
                      <PawPrint className="h-3 w-3" />
                      {t('nutritionAdmin.normalization.pet')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-1">{t('nutritionAdmin.normalization.textToMerge')}</p>
                <code className="text-lg font-medium">{selectedAlias.alias_text}</code>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('nutritionAdmin.normalization.appearsInRecords', { count: selectedAlias.occurrence_count })}
                </p>
              </div>

              {selectedAlias.is_farm_animal ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('nutritionAdmin.normalization.feedCategory')}</label>
                  <Select 
                    value={selectedFeedCategory} 
                    onValueChange={setSelectedFeedCategory}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('nutritionAdmin.normalization.selectFeedCategoryPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {feedCategories?.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          <div className="flex flex-col">
                            <span>{category.name}</span>
                            <span className="text-xs text-muted-foreground">{category.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('nutritionAdmin.normalization.officialBrand')}</label>
                  <Select 
                    value={selectedCanonicalBrand} 
                    onValueChange={setSelectedCanonicalBrand}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('nutritionAdmin.normalization.selectBrandPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {brands?.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id.toString()}>
                          <div className="flex items-center gap-2">
                            <span>{brand.name}</span>
                            {brand.is_prescription && (
                              <Badge variant="outline" className="text-xs">Rx</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleMerge}
              disabled={!canSubmit || isPending}
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : selectedAlias?.is_farm_animal ? (
                <Wheat className="h-4 w-4 mr-2" />
              ) : (
                <Merge className="h-4 w-4 mr-2" />
              )}
              {selectedAlias?.is_farm_animal 
                ? t('nutritionAdmin.normalization.categorizeFeedButton')
                : t('nutritionAdmin.normalization.mergeBrandButton')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NutritionBrandNormalization;